import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPositions, getOrders, getAlpacaAccountId, getAccount } from '../lib/alpaca';
import { getReferralStats } from '../lib/cerebro';

const ACHIEVEMENTS = [
  {
    id: 'first_investment',
    title: 'Primera inversión',
    desc: 'Compra tu primera acción o ETF',
    icon: '🎯',
    category: 'inicio',
    check: (data) => data.totalOrders > 0,
  },
  {
    id: 'plan_activated',
    title: 'Plan activado',
    desc: 'Completa el cuestionario de inversión',
    icon: '📋',
    category: 'inicio',
    check: (data) => data.hasProfile,
  },
  {
    id: 'autopilot',
    title: 'Piloto automático',
    desc: 'Activa un plan de inversion automatica',
    icon: '🤖',
    category: 'inicio',
    check: (data) => data.roboUsed,
    link: '/dashboard/investments?tab=piloto',
  },
  {
    id: 'diversified',
    title: 'Diversificado',
    desc: 'Ten 5 o más activos diferentes',
    icon: '🧩',
    category: 'estrategia',
    check: (data) => data.totalPositions >= 5,
  },
  {
    id: 'explorer',
    title: 'Explorador',
    desc: 'Revisa 10 acciones diferentes',
    icon: '🔍',
    category: 'estrategia',
    check: (data) => (data.viewedStocks || 0) >= 10,
  },
  {
    id: 'club_100',
    title: 'Club $100',
    desc: 'Tu portafolio llega a $100 USD',
    icon: '💯',
    category: 'milestone',
    check: (data) => data.equity >= 100,
  },
  {
    id: 'club_500',
    title: 'Club $500',
    desc: 'Tu portafolio llega a $500 USD',
    icon: '🏅',
    category: 'milestone',
    check: (data) => data.equity >= 500,
  },
  {
    id: 'club_1000',
    title: 'Club $1,000',
    desc: 'Tu portafolio llega a $1,000 USD',
    icon: '🏆',
    category: 'milestone',
    check: (data) => data.equity >= 1000,
  },
  {
    id: 'ambassador',
    title: 'Embajador',
    desc: '1 amigo invierte con tu codigo',
    reward: '1 compra sin comision',
    icon: '🤝',
    category: 'social',
    check: (data) => (data.referrals || 0) >= 1,
  },
  {
    id: 'influencer',
    title: 'Influencer',
    desc: '5 amigos invierten con tu codigo',
    reward: '5 compras sin comision',
    icon: '⭐',
    category: 'social',
    check: (data) => (data.referrals || 0) >= 5,
  },
  {
    id: 'legend',
    title: 'Leyenda',
    desc: '10 amigos invierten con tu codigo',
    reward: '$20 USD invertidos gratis en tu portafolio',
    icon: '👑',
    category: 'social',
    check: (data) => (data.referrals || 0) >= 10,
  },
];

const LEVELS = [
  { name: 'Novato', minXP: 0, color: '#9CA3AF' },
  { name: 'Inversionista', minXP: 3, color: '#3d8bfd' },
  { name: 'Trader', minXP: 6, color: '#3d8bfd' },
  { name: 'Experto', minXP: 9, color: '#F59E0B' },
  { name: 'Leyenda', minXP: 11, color: '#DC2626' },
];

const LEVEL_MARK = {
  Novato: '🌱',
  Inversionista: '📈',
  Trader: '⚡',
  Experto: '👑',
  Leyenda: '🏆',
};

function getLevel(unlockedCount) {
  for (let i = LEVELS.length - 1; i >= 0; i--) {
    if (unlockedCount >= LEVELS[i].minXP) return LEVELS[i];
  }
  return LEVELS[0];
}

function getNextLevel(unlockedCount) {
  for (let i = 0; i < LEVELS.length; i++) {
    if (unlockedCount < LEVELS[i].minXP) return LEVELS[i];
  }
  return null;
}

function getStreak() {
  try {
    const data = JSON.parse(localStorage.getItem('fp_invest_streak') || '{}');
    return data.weeks || 0;
  } catch { return 0; }
}

function trackStockView(symbol) {
  try {
    const viewed = JSON.parse(localStorage.getItem('fp_viewed_stocks') || '[]');
    if (!viewed.includes(symbol)) {
      viewed.push(symbol);
      localStorage.setItem('fp_viewed_stocks', JSON.stringify(viewed));
    }
  } catch {}
}

function getViewedCount() {
  try {
    return JSON.parse(localStorage.getItem('fp_viewed_stocks') || '[]').length;
  } catch { return 0; }
}

function CheckMark({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="var(--ro-good, #3dd68c)" />
      <polyline
        points="16 8.5 10.5 14 8 11.5"
        fill="none"
        stroke="#0b1220"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LockMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0110 0v4" />
    </svg>
  );
}

function ReferralStep({ num, title, desc }) {
  return (
    <div className="rendim-ola__step">
      <div className="rendim-ola__step-num">{num}</div>
      <div>
        <div className="rendim-ola__step-title">{title}</div>
        <div className="rendim-ola__step-desc">{desc}</div>
      </div>
    </div>
  );
}

function ReferralTier({ icon, title, requirement, reward, achieved }) {
  return (
    <div className={`rendim-ola__tier ${achieved ? 'is-on' : 'is-off'}`}>
      <span className="rendim-ola__tier-icon" aria-hidden="true">{icon}</span>
      <div className="rendim-ola__tier-body">
        <div className="rendim-ola__tier-row">
          <span className="rendim-ola__tier-title">{title}</span>
          <span className="rendim-ola__tier-req">({requirement})</span>
          {achieved && <CheckMark size={12} />}
        </div>
        <div className="rendim-ola__tier-reward">{reward}</div>
      </div>
    </div>
  );
}

export default function Achievements({ userEmail }) {
  const navigate = useNavigate();
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCat, setSelectedCat] = useState('all');
  const [copied, setCopied] = useState(null);
  const [referralData, setReferralData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const accountId = getAlpacaAccountId(userEmail);
        let equity = 0, totalPositions = 0, totalOrders = 0;

        try {
          const account = await getAccount(accountId);
          equity = parseFloat(account.equity || 0);
        } catch {}

        try {
          const positions = await getPositions(accountId);
          totalPositions = positions.length;
        } catch {}

        try {
          const orders = await getOrders('all', 100, accountId);
          totalOrders = orders.length;
        } catch {}

        // Fetch referral data from API
        let referrals = 0;
        let refCredits = 0;
        let refCode = '';
        let refReferrals = [];
        try {
          const refResult = await getReferralStats();
          if (refResult.success && refResult.data) {
            referrals = refResult.data.completedCount || 0;
            refCredits = refResult.data.freeCommissionCredits || 0;
            refCode = refResult.data.referralCode || '';
            refReferrals = refResult.data.referrals || [];
            setReferralData(refResult.data);
          }
        } catch {
          // Fallback: no referral data available
        }

        const hasProfile = !!localStorage.getItem('fp_investor_profile');
        const roboUsed = !!localStorage.getItem('fp_robo_used');
        const viewedStocks = getViewedCount();
        const streak = getStreak();

        const data = { equity, totalPositions, totalOrders, hasProfile, roboUsed, viewedStocks, streak, referrals };

        const results = ACHIEVEMENTS.map((a) => ({
          ...a,
          unlocked: a.check(data),
        }));

        setAchievements(results);
      } catch (e) {
        console.error('achievements:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [userEmail]);

  const unlocked = achievements.filter((a) => a.unlocked);
  const locked = achievements.filter((a) => !a.unlocked);
  const level = getLevel(unlocked.length);
  const nextLevel = getNextLevel(unlocked.length);
  const streak = getStreak();

  const categories = [
    { id: 'all', label: 'Todos' },
    { id: 'inicio', label: 'Inicio' },
    { id: 'estrategia', label: 'Estrategia' },
    { id: 'milestone', label: 'Milestones' },
    { id: 'social', label: 'Social' },
  ];

  const filtered = selectedCat === 'all' ? achievements : achievements.filter((a) => a.category === selectedCat);

  if (loading) {
    return (
      <div className="rendim-ola space-y-4">
        <div className="rendim-ola__skel h-24 rounded-2xl" />
        <div className="rendim-ola__skel h-40 rounded-2xl" />
      </div>
    );
  }

  const referralCode = referralData?.referralCode || `FLOWEX-${(userEmail || 'USER').slice(0, 4).toUpperCase()}`;

  return (
    <div className="rendim-ola space-y-4">
      {/* Level + Streak banner */}
      <div className="rendim-ola__hero">
        <div className="rendim-ola__hero-top">
          <div className="rendim-ola__hero-left">
            <div className="rendim-ola__level-mark" aria-hidden="true">
              {LEVEL_MARK[level.name] || '📈'}
            </div>
            <div>
              <div className="rendim-ola__level-name">{level.name}</div>
              <div className="rendim-ola__level-sub">{unlocked.length} de {achievements.length} logros</div>
            </div>
          </div>
          {streak > 0 && (
            <div className="rendim-ola__streak">
              <div className="rendim-ola__streak-n">🔥 {streak}</div>
              <div className="rendim-ola__streak-l">{streak === 1 ? 'semana' : 'semanas'}</div>
            </div>
          )}
        </div>

        {nextLevel && (
          <div>
            <div className="rendim-ola__progress-meta">
              <span>{level.name}</span>
              <span>{nextLevel.name} ({nextLevel.minXP - unlocked.length} logros más)</span>
            </div>
            <div className="rendim-ola__progress-track">
              <div
                className="rendim-ola__progress-fill"
                style={{ width: `${Math.min(100, (unlocked.length / nextLevel.minXP) * 100)}%` }}
              />
            </div>
          </div>
        )}
        {!nextLevel && (
          <div className="rendim-ola__max">¡Nivel máximo alcanzado!</div>
        )}
      </div>

      {/* Category filters */}
      <div className="rendim-ola__chips">
        {categories.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setSelectedCat(c.id)}
            className={`rendim-ola__tchip ${selectedCat === c.id ? 'on' : ''}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Achievements grid */}
      <div className="rendim-ola__grid">
        {filtered.map((a) => (
          <div
            key={a.id}
            onClick={() => a.link && navigate(a.link)}
            className={`rendim-ola__card ${a.unlocked ? 'is-on' : 'is-off'} ${a.link ? 'is-link' : ''}`}
            role={a.link ? 'button' : undefined}
            tabIndex={a.link ? 0 : undefined}
            onKeyDown={(e) => {
              if (a.link && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                navigate(a.link);
              }
            }}
          >
            <div className={`rendim-ola__icon ${a.unlocked ? '' : 'is-locked'}`}>
              {a.icon}
            </div>
            <div className="rendim-ola__card-body">
              <div className="rendim-ola__card-title-row">
                <span className="rendim-ola__card-title">{a.title}</span>
                {a.unlocked && <CheckMark />}
              </div>
              <div className="rendim-ola__card-desc">{a.desc}</div>
              {a.reward && <div className="rendim-ola__card-reward">{a.reward}</div>}
            </div>
            {!a.unlocked && a.link && (
              <span className="rendim-ola__go">Ir ›</span>
            )}
            {!a.unlocked && !a.link && (
              <span className="rendim-ola__lock"><LockMark /></span>
            )}
          </div>
        ))}
      </div>

      {/* Programa Invita y Gana */}
      <div className="rendim-ola__invite">
        <div className="rendim-ola__invite-head">
          <div className="rendim-ola__invite-kicker">Programa de referidos</div>
          <h3 className="rendim-ola__invite-title">Invita y Gana</h3>
          <p className="rendim-ola__invite-lead">
            Comparte tu codigo. Cuando tu amigo haga su primera inversion, ambos ganan.
          </p>
        </div>

        <div className="rendim-ola__invite-body">
          <div>
            <div className="rendim-ola__section-label">Como funciona</div>
            <div className="space-y-3">
              <ReferralStep num="1" title="Comparte tu codigo" desc="Envialo por WhatsApp, redes sociales o como quieras." />
              <ReferralStep num="2" title="Tu amigo se registra" desc="Se registra en FlowexPay usando tu codigo de invitacion." />
              <ReferralStep num="3" title="Tu amigo hace su primera inversion" desc="Cuando invierte por primera vez, ambos reciben su recompensa." />
            </div>
          </div>

          <div className="rendim-ola__reward-pair">
            <div className="rendim-ola__wash rendim-ola__wash--green">
              <div className="rendim-ola__wash-kicker">Tu amigo recibe</div>
              <div className="rendim-ola__wash-title">Primera compra sin comision</div>
              <div className="rendim-ola__wash-sub">Se ahorra la comision en su primer trade</div>
            </div>
            <div className="rendim-ola__wash rendim-ola__wash--blue">
              <div className="rendim-ola__wash-kicker">Tu recibes</div>
              <div className="rendim-ola__wash-title">Tu siguiente compra sin comision</div>
              <div className="rendim-ola__wash-sub">Por cada amigo que invierta</div>
            </div>
          </div>

          <div>
            <div className="rendim-ola__section-label">Mientras mas invites, mas ganas</div>
            <div className="space-y-2">
              <ReferralTier
                icon="🤝" title="Embajador" requirement="1 amigo"
                reward="1 compra sin comision"
                achieved={(referralData?.completedCount || 0) >= 1}
              />
              <ReferralTier
                icon="⭐" title="Influencer" requirement="5 amigos"
                reward="5 compras sin comision"
                achieved={(referralData?.completedCount || 0) >= 5}
              />
              <ReferralTier
                icon="👑" title="Leyenda" requirement="10 amigos"
                reward="$20 USD invertidos gratis en tu portafolio"
                achieved={(referralData?.completedCount || 0) >= 10}
              />
            </div>
          </div>

          <div>
            <div className="rendim-ola__section-label">Tu codigo de invitacion</div>
            <div className="rendim-ola__code-row">
              <div className="rendim-ola__code-box">{referralCode}</div>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard?.writeText(referralCode);
                  setCopied('code');
                  setTimeout(() => setCopied(null), 2000);
                }}
                className="rendim-ola__btn-accent"
              >
                {copied === 'code' ? 'Copiado' : 'Copiar'}
              </button>
            </div>

            <button
              type="button"
              onClick={() => {
                const msg = `Invierte en acciones de EE.UU. desde Colombia con FlowexPay. Tu primera compra es sin comision con mi codigo: ${referralCode}\n\nhttps://beta.flowexpay.com/register?ref=${encodeURIComponent(referralCode)}`;
                navigator.clipboard?.writeText(msg);
                setCopied('msg');
                setTimeout(() => setCopied(null), 2000);
              }}
              className="rendim-ola__btn-ghost"
            >
              {copied === 'msg' ? 'Mensaje copiado' : 'Copiar mensaje para compartir'}
            </button>
          </div>

          {referralData && (referralData.completedCount > 0 || referralData.pendingCount > 0) && (
            <div className="rendim-ola__stats">
              <div className="rendim-ola__stats-grid">
                <div>
                  <div className="rendim-ola__stat-n is-good">{referralData.completedCount}</div>
                  <div className="rendim-ola__stat-l">completado{referralData.completedCount !== 1 ? 's' : ''}</div>
                </div>
                <div>
                  <div className="rendim-ola__stat-n is-warn">{referralData.pendingCount}</div>
                  <div className="rendim-ola__stat-l">pendiente{referralData.pendingCount !== 1 ? 's' : ''}</div>
                </div>
                <div>
                  <div className="rendim-ola__stat-n is-accent">{referralData.freeCommissionCredits}</div>
                  <div className="rendim-ola__stat-l">compras gratis</div>
                </div>
              </div>
              {referralData.referrals && referralData.referrals.length > 0 && (
                <div className="rendim-ola__ref-list">
                  {referralData.referrals.slice(0, 5).map((r) => (
                    <div key={r.id} className="rendim-ola__ref-row">
                      <span className="rendim-ola__ref-name">{r.referredName}</span>
                      <span className={`rendim-ola__badge ${r.status === 'completed' ? 'is-good' : 'is-warn'}`}>
                        {r.status === 'completed' ? 'Invirtio' : 'Pendiente'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="rendim-ola__rules">
            <p>Solo se cuenta como referido exitoso cuando tu amigo hace su primera inversion real.</p>
            <p>Las compras sin comision se aplican automaticamente en tu siguiente trade.</p>
            <p>El bono de $20 USD del nivel Leyenda se invierte en tu portafolio asignado.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export { trackStockView, getViewedCount, ACHIEVEMENTS };
